import { findIndex, pick } from 'lodash-es';
import {
  FILE_EXTENSION_PNG_IMAGE,
  HAZARD_ICONS_PATH,
  HAZARD_TYPE_ENV_HAZARDS,
  recordOptionHighlightColor,
  recordOptionType,
  VIEW_MODE,
} from './constants';
import { history } from './store';
import { commonActions } from './behavior/common';
import { isChemicalSafetyExpert } from './behavior/init/selectors';
import { cloneDeep } from 'lodash-es';

export const mapRequiredSelectProps = (text, value, key) => {
  const newObj = {};
  newObj.text = text;
  newObj.value = value;
  newObj.key = key;
  return newObj;
};

export const getObjPropRecursively = (obj, props) => {
  let result = obj;
  if (props && obj) {
    const propsPath = props.split('.');
    propsPath.forEach(subProp => {
      result = result != null && result.hasOwnProperty(subProp) ? result[subProp] : null;
    });
  }
  return result;
};

export const setObjProp = (obj, props, value) => {
  let innerObj = obj;
  if (props && obj) {
    const propsPath = props.split('.');
    propsPath.forEach((subProp, index) => {
      if (index == propsPath.length - 1) {
        innerObj[subProp] = value;
      } else {
        if (!innerObj.hasOwnProperty(subProp) || innerObj[subProp] == null) {
          innerObj[subProp] = {};
        }
        innerObj = innerObj[subProp];
      }
    });
  }
  return obj;
};

export const pushToObjProp = (obj, props, value) => {
  let innerObj = obj;
  if (props && obj) {
    const propsPath = props.split('.');
    propsPath.forEach((subProp, index) => {
      if (index == propsPath.length - 1) {
        innerObj[subProp].push(value);
      } else {
        if (!innerObj.hasOwnProperty(subProp)) {
          innerObj[subProp] = {};
        }
        innerObj = innerObj[subProp];
      }
    });
  }
  return obj;
};

export const removeFromObjProp = (obj, props, indexToRemove) => {
  let innerObj = obj;
  if (props && obj) {
    const propsPath = props.split('.');
    propsPath.forEach((subProp, index) => {
      if (index == propsPath.length - 1) {
        innerObj[subProp].splice(indexToRemove, 1);
      } else {
        if (!innerObj.hasOwnProperty(subProp)) {
          innerObj[subProp] = {};
        }
        innerObj = innerObj[subProp];
      }
    });
  }
  return obj;
};

export const manipulateValuesBeforeSubmission = (
  values,
  processedNotificationBody,
  recipients,
  user,
  i18n,
  markAsChecked
) => {
  let inventory = values;
  let chemicalProduct =
    Object.keys(values).length == 1 && values.hasOwnProperty('chemicalProduct')
      ? values.chemicalProduct
      : null;

  if (!isChemicalSafetyExpert(user)) {
    if (chemicalProduct) {
      chemicalProduct.validateDte = null;
      chemicalProduct.validateUser = null;
    } else {
      inventory.checkCseUser = null;
      inventory.checkCseDte = null;
      cleanUpChemRiskCheckCse(inventory);
    }
  } else if (!chemicalProduct && !isCheckCseRequiredForChemRisk(values.chemicalRiskAttributes)) {
    cleanUpChemRiskCheckCse(inventory);
  }

  //reduce payload when (and only when) opening the dialog, by not sending the base64.
  const inventoryDeepClone = cloneDeep(inventory);
  if (!processedNotificationBody && inventoryDeepClone && !chemicalProduct) {
    inventoryDeepClone.files.forEach(f => (f.fileData.fileBase64 = null));
    inventoryDeepClone.chemicalProduct.sdsFiles.forEach(f => {
      f.fileData.fileBase64 = null;
      f.fileData.size = null;
    });
  }
  const chemicalProductDeepClone = cloneDeep(chemicalProduct);
  if (!processedNotificationBody && chemicalProductDeepClone && chemicalProduct) {
    chemicalProductDeepClone.sdsFiles.forEach(f => (f.fileData.fileBase64 = null));
  }

  let saveRequestDto = {
    inventory: inventoryDeepClone,
    chemicalProduct: chemicalProductDeepClone,
    processedNotificationBody,
    recipients,
    lang: i18n.language,
    markAsChecked,
  };
  return saveRequestDto;
};

const cleanUpChemRiskCheckCse = inventory => {
  inventory.chemicalRiskAttributes.checkCseUser = null;
  inventory.chemicalRiskAttributes.checkCseDte = null;
};

const getObjPropsJoined = (object, props) => {
  const result = [];
  if (props) {
    props.forEach(prop => {
      const v = getObjPropRecursively(object, prop);
      if (v) {
        result.push(v);
      }
    });
  } else {
    result.push(object);
  }
  return result.join(' ,');
};

/**
 * @param list are all the available options
 * @param defaultValue is added as the only option if list is not declared, this allows the value to be displayed
 * @param text is the property to be displayed as text
 * @returns the available options mapped as required by select field component
 */
export const getOptions = (list, defaultValue = null, propsAsText = ['value']) => {
  const getValueKey = o => (o.id ? o.id : o);
  if (list) {
    return list.map(o =>
      mapRequiredSelectProps(getObjPropsJoined(o, propsAsText), getValueKey(o), getValueKey(o))
    );
  }
  if (defaultValue) {
    return [defaultValue].map(o =>
      mapRequiredSelectProps(getObjPropsJoined(o, propsAsText), getValueKey(o), getValueKey(o))
    );
  }
  return [];
};

/**
 *
 * @param list are all the available options
 * @param riskTypeCode is the code based on which the list is filtered
 * @param defaultValue is added as the only option if list is not declared, this allows the value to be displayed
 * @returns the available risk options filtered and mapped as required by select field component
 */
export const getRiskOptions = (
  list,
  riskTypeCode,
  defaultValue = null,
  propsAsText = ['value']
) => {
  let filteredList = list;
  if (list) {
    filteredList = list.filter(p => (riskTypeCode ? p.riskType.code == riskTypeCode : true));
  }
  return getOptions(filteredList, defaultValue, propsAsText);
};

export const modeChangedFromView = (props, newProps) =>
  props.mode != newProps.mode && props.mode == VIEW_MODE;

export const getErrorMessage = error => {
  if (
    error &&
    error.response &&
    error.response.data &&
    error.response.data.message &&
    error.response.data.message.startsWith('message.error')
  ) {
    return error.response.data.message;
  }
  if (error && error.message) {
    return error.message;
  }
  if (error) {
    return error;
  }
  return null;
};

export const updateByKey = (arr, updatedObject, matcherArgs) => {
  const data = [...arr];
  const idx = findIndex(data, pick(updatedObject, matcherArgs));
  data[idx] = updatedObject;
  return data;
};

/* highlighting rows that were cloned/deleted */
export const highlightComponent = (componentID, onRecordOptionType) => {
  if (document.getElementById(componentID) !== null) {
    const highlightComponent = document.getElementById(componentID);
    const originalBgColor = highlightComponent.style.background;
    highlightComponent.style.transition = 'all 2s';

    if (onRecordOptionType === recordOptionType.delete) {
      highlightComponent.style.background = recordOptionHighlightColor.delete;
    } else if (onRecordOptionType === recordOptionType.clone) {
      highlightComponent.style.background = recordOptionHighlightColor.clone;
    }

    setTimeout(() => {
      highlightComponent.style.background = originalBgColor;
    }, 5000);
  }
};

export const conditionToDisplayEnvInfoAndEnvRiskLevel = values => {
  const { chemicalProduct, quantity } = values;

  const stateCondition = chemicalProduct && isLiquid(chemicalProduct.state); //Liquid

  const quantityCondition =
    quantity >= 100 ||
    (quantity >= 20 && chemicalProduct && environmentalHazardExists(chemicalProduct.hazards));

  return stateCondition && quantityCondition;
};

export const isLiquid = state => {
  const stateCondition = state && state.id == 2; //Liquid
  return stateCondition;
};

export const environmentalHazardExists = hazards =>
  hazards && hazards.some(h => h.hazardType.code == HAZARD_TYPE_ENV_HAZARDS);

export const isCheckCseRequiredForChemRisk = chemicalRiskAttributes =>
  chemicalRiskAttributes &&
  chemicalRiskAttributes.chemRiskLevel &&
  chemicalRiskAttributes.chemRiskLevel.chemRiskLevel &&
  chemicalRiskAttributes.chemRiskLevel.chemRiskLevel.id >= 4;

export const getQuantityType = chemicalProduct => {
  if (chemicalProduct && [1, 3].includes(chemicalProduct.state.id)) {
    return 'inventory.details.page.chemical.container.quantity.input.label.kilos';
  }
  if (chemicalProduct && [2].includes(chemicalProduct.state.id)) {
    return 'inventory.details.page.chemical.container.quantity.input.label.liters';
  }
  return '';
};

export const lowercaseNormalize = value =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

export const getLocationTypeLabel = locationInfo => {
  const locationInfoParts = locationInfo.split(/[/-]/);
  if (locationInfoParts.length === 1) {
    return 'location.info.building.label';
  }
  if (locationInfoParts.length === 2) {
    return 'location.info.floor.label';
  }
  if (locationInfoParts.length === 3) {
    return 'location.info.room.label';
  }
};

export const getInventoryTitle = (values, t) => {
  if (values) {
    const titleComponents = [];
    if (values.location && values.location.locationInfo) {
      titleComponents.push(
        t(getLocationTypeLabel(values.location.locationInfo)).concat(
          ' ',
          values.location.locationInfo
        )
      );
    }
    if (values.asset && values.asset.equipmentCode) {
      titleComponents.push(
        t('inventory.details.page.equipment.container.asset.input.label').concat(
          ' ',
          values.asset.equipmentCode
        )
      );
    } else if (values.asset && values.asset.position) {
      titleComponents.push(
        t('inventory.details.page.equipment.container.asset.position.input.label').concat(
          ' ',
          values.asset.position
        )
      );
    }
    if (values.quantity !== null && values.quantity !== undefined) {
      titleComponents.push(
        t('inventory.details.page.chemical.container.quantity.input.label').concat(
          ' ',
          values.quantity,
          ' ',
          t(getQuantityType(values.chemicalProduct))
        )
      );
    }

    return titleComponents.join(', ');
  } else {
    return '';
  }
};

export const isHexColorBright = hexColor => {
  if (hexColor) {
    const hex = hexColor.replace('#', '');
    const c_r = parseInt(hex.substr(0, 2), 16);
    const c_g = parseInt(hex.substr(2, 2), 16);
    const c_b = parseInt(hex.substr(4, 2), 16);
    const brightness = (c_r * 299 + c_g * 587 + c_b * 114) / 1000;
    return brightness > 155;
  }
  return false;
};

export const getSelectedChemProdProps = chemProd => ({
  id: chemProd.id,
  tradeName: chemProd.tradeName,
  chemicalName: chemProd.chemicalName,
});

export const sharedDataViewToFilterState = (sharedDataView, filterProps) => {
  const visibleCols = sharedDataView.visibleColumns;
  const visibleFlds = sharedDataView.visibleFields;

  return {
    filterValues: JSON.parse(sharedDataView.filterValues || '{}'),
    search: sharedDataView.search,
    visibleColumns: filterProps.possibleColumnsList
      .filter(c => visibleCols.includes(c.key))
      .sort((a, b) => visibleCols.indexOf(a.key) - visibleCols.indexOf(b.key)),
    visibleFields: filterProps.possibleFieldsList
      .filter(c => visibleFlds.includes(c.key))
      .sort((a, b) => visibleFlds.indexOf(a.key) - visibleFlds.indexOf(b.key)),
    pageSize: sharedDataView.pageSize,
    sort: {
      sortField: sharedDataView.sortField,
      sortDirection: sharedDataView.sortDirection,
    },
    dataViewId: 0,
  };
};

export const goToErrorPage = ({ response }) => {
  if (response.status === 401 || response.status === 403) {
    history.replace('/forbidden');
    history.go(0);
  } else if (response.data !== 'dataviewError') {
    history.push('/error');
  }
  return commonActions.toggleErrorPage(response.data);
};

export const getHazardIconPath = id =>
  window.location.origin + HAZARD_ICONS_PATH + id + FILE_EXTENSION_PNG_IMAGE;

export const getChangedProperties = (before, after) => {
  if (before && Object.keys(before)?.length > 0) {
    const keys = Object.keys(before);
    const diffs = {};
    keys.forEach(key => {
      if (before[key] != after[key]) {
        diffs[key] = { old: before[key], new: after[key] };
      }
    });
    return diffs;
  }
};

export const mergeDictionariesReducer = (result, current) => Object.assign(result, current);
export const getCurrentLocationBaseUrl = () =>
  `${window.location.protocol}//${window.location.hostname}${
    window.location.port ? `:${window.location.port}` : ''
  }`;

export const isPrivateDataField = fieldName =>
  [
    'GEN_CONTACTS',
    'GEN_CREATE_USER',
    'GEN_DELETE_USER',
    'GEN_UPDATE_USER',
    'GEN_CHECK_USER',
    'GEN_CHECK_CSE_USER',
    'GEN_VALIDATE_USER',
    'CRA_CHECK_CSE_USER',
    'HIST_CREATE_USER',
  ].includes(fieldName);

export const identityFunction = x => x;

export const setRowSize = (inputText, minRow, numberOfCharactersPerLine) => {
  const rows = Math.max(minRow, Math.ceil(inputText.length / numberOfCharactersPerLine));
  return rows;
};
